/**
 * 应用更新检查 Hook
 *
 * 功能：
 * - 应用启动后延迟自动检查更新（默认 1 分钟，避免与启动流程冲突）
 * - 24 小时内已检查过则跳过自动检查（避免频繁打扰用户）
 * - 用户已忽略的版本不再弹出提示
 * - 桌面端检测到新版本后自动后台下载，下载完成后提示用户安装
 * - 提供 checkNow 方法供 Settings 页面手动触发
 *
 * 使用方式：
 *   const { updateInfo, isChecking, checkNow, dismiss } = useUpdateChecker({
 *     checkOnMount: true,
 *     delayMs: 60000,
 *   })
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import {
  checkForUpdate,
  performUpdate,
  performBackgroundDownload,
  performInstallDownloaded,
  getAppVersion,
  type UpdateInfo,
  type DownloadProgress,
} from '../services/UpdateService'
import { isTauriMobilePlatform } from '../utils/mobile'

const LAST_CHECK_KEY = 'last_update_check_time'
const DISMISSED_VERSION_KEY = 'dismissed_update_version'
const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000 // 24 小时
const DOWNLOAD_STATUS_KEY = 'update_download_status'
const PENDING_VERSION_KEY = 'update_pending_version'
const UPDATE_STATUS_EVENT = 'update-status-change'

export type DownloadStatus = 'idle' | 'downloading' | 'downloaded' | 'installing'

interface UseUpdateCheckerOptions {
  checkOnMount?: boolean
  delayMs?: number
  skipIfCheckedRecently?: boolean
  autoDownloadOnDesktop?: boolean // 桌面端检测到更新后是否自动后台下载
}

interface UseUpdateCheckerResult {
  updateInfo: UpdateInfo | null
  isChecking: boolean
  isUpdating: boolean
  error: string | null
  progress: DownloadProgress | null
  downloadStatus: DownloadStatus
  checkNow: () => Promise<void>
  dismiss: () => void
  applyUpdate: () => Promise<void>
  startDownload: () => Promise<void>
  installDownloaded: () => Promise<void>
  retryDownload: () => Promise<void>
}

export function useUpdateChecker(options: UseUpdateCheckerOptions = {}): UseUpdateCheckerResult {
  const {
    checkOnMount = true,
    delayMs = 60000,
    skipIfCheckedRecently = true,
    autoDownloadOnDesktop = true, // 默认开启桌面端自动后台下载
  } = options

  // ==== 初始化：从 localStorage 恢复持久化状态 ====
  const getInitialDownloadStatus = (): DownloadStatus => {
    const persisted = localStorage.getItem(DOWNLOAD_STATUS_KEY) as DownloadStatus | null
    if (persisted === 'downloaded') return 'downloaded'
    if (persisted === 'downloading') return 'idle' // 上次下载中断，重置为 idle
    return 'idle'
  }

  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<DownloadProgress | null>(null)
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>(getInitialDownloadStatus)

  // 使用 ref 存储最新值，避免闭包陷阱
  const mountedRef = useRef(true)
  const checkedRef = useRef(false) // 确保启动时只自动检查一次
  const autoDownloadStartedRef = useRef(false) // 防止重复启动后台下载
  const downloadStatusRef = useRef<DownloadStatus>(getInitialDownloadStatus()) // 同步状态 ref
  const updateInfoRef = useRef<UpdateInfo | null>(null) // 同步 updateInfo ref

  const isMobile = isTauriMobilePlatform()

  // ==== 启动时清理过期的 localStorage 状态 ====
  // 当应用版本号变化时，清理旧版本残留的 dismissed 记录和下载状态
  useEffect(() => {
    try {
      const currentVersion = getAppVersion()
      const dismissedVersion = localStorage.getItem(DISMISSED_VERSION_KEY)
      const pendingVersion = localStorage.getItem(PENDING_VERSION_KEY)
      const downloadStatus = localStorage.getItem(DOWNLOAD_STATUS_KEY)

      console.log('[useUpdateChecker] 启动状态检查:', {
        currentVersion,
        dismissedVersion,
        pendingVersion,
        downloadStatus,
      })

      // 清理 1: 如果 dismissed 的版本 <= 当前版本，清除它
      // 这样升级后，旧版本的 dismissed 记录不会影响新版本的更新检查
      if (dismissedVersion && compareVersions(dismissedVersion, currentVersion) <= 0) {
        console.log('[useUpdateChecker] 清理过期的 dismissed 记录:', {
          dismissedVersion,
          currentVersion,
        })
        localStorage.removeItem(DISMISSED_VERSION_KEY)
      }

      // 清理 2: 如果 pendingVersion != currentVersion，且下载状态不是 downloaded，则清除
      if (pendingVersion && pendingVersion !== currentVersion) {
        if (downloadStatus !== 'downloaded') {
          console.log('[useUpdateChecker] 清理过期的下载状态:', {
            pendingVersion,
            currentVersion,
          })
          localStorage.removeItem(DOWNLOAD_STATUS_KEY)
          localStorage.removeItem(PENDING_VERSION_KEY)
        }
      }

      // 清理 3: 如果下载状态是 installing（上次安装中断），重置为 idle
      if (downloadStatus === 'installing' || downloadStatus === 'downloading') {
        console.log('[useUpdateChecker] 重置异常的下载状态:', downloadStatus)
        localStorage.removeItem(DOWNLOAD_STATUS_KEY)
        localStorage.removeItem(PENDING_VERSION_KEY)
      }
    } catch {
      // 忽略 localStorage 错误（可能是隐私模式等）
    }
  }, [])

  // ==== 持久化辅助函数 ====
  const persistDownloadStatus = useCallback((status: DownloadStatus, pendingVersion?: string) => {
    if (status === 'idle') {
      localStorage.removeItem(DOWNLOAD_STATUS_KEY)
      localStorage.removeItem(PENDING_VERSION_KEY)
    } else {
      localStorage.setItem(DOWNLOAD_STATUS_KEY, status)
      if (pendingVersion) {
        localStorage.setItem(PENDING_VERSION_KEY, pendingVersion)
      }
    }
  }, [])

  // ==== 广播辅助函数：CustomEvent（同页面多实例同步）====
  const broadcastStatusChange = useCallback(() => {
    try {
      window.dispatchEvent(
        new CustomEvent(UPDATE_STATUS_EVENT, {
          detail: {
            downloadStatus: downloadStatusRef.current,
            updateInfo: updateInfoRef.current,
          },
        })
      )
    } catch {
      /* ignore */
    }
  }, [])

  // 同步 ref
  useEffect(() => {
    downloadStatusRef.current = downloadStatus
    persistDownloadStatus(downloadStatus, updateInfo?.version)
    broadcastStatusChange()
  }, [downloadStatus, updateInfo?.version, persistDownloadStatus, broadcastStatusChange])

  useEffect(() => {
    updateInfoRef.current = updateInfo
    broadcastStatusChange()
  }, [updateInfo, broadcastStatusChange])

  // ==== 监听外部状态变更（CustomEvent + storage 事件）====
  useEffect(() => {
    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ downloadStatus?: DownloadStatus; updateInfo?: UpdateInfo | null }>
      const incomingStatus = customEvent.detail?.downloadStatus
      const incomingInfo = customEvent.detail?.updateInfo

      if (incomingStatus !== undefined && incomingStatus !== downloadStatusRef.current) {
        downloadStatusRef.current = incomingStatus
        setDownloadStatus(incomingStatus)
      }

      if (incomingInfo !== undefined) {
        const hasCurrentInfo = updateInfoRef.current !== null
        const hasIncomingInfo = incomingInfo !== null && incomingInfo?.hasUpdate
        const versionChanged = incomingInfo?.version !== updateInfoRef.current?.version
        // 只在状态变化或版本变化时才同步，避免循环触发
        if ((!hasCurrentInfo && hasIncomingInfo) || (hasCurrentInfo && !hasIncomingInfo) || versionChanged) {
          updateInfoRef.current = incomingInfo || null
          setUpdateInfo(incomingInfo || null)
        }
      }
    }

    // 跨标签页/窗口同步
    const handleStorage = (e: StorageEvent) => {
      if (e.key === DOWNLOAD_STATUS_KEY) {
        const newStatus = (e.newValue as DownloadStatus | null) ?? 'idle'
        if (newStatus !== downloadStatusRef.current) {
          // 'downloading' 跨标签不保留
          const effective = newStatus === 'downloading' ? 'idle' : newStatus
          downloadStatusRef.current = effective
          setDownloadStatus(effective)
        }
      }
    }

    window.addEventListener(UPDATE_STATUS_EVENT, handleCustomEvent)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener(UPDATE_STATUS_EVENT, handleCustomEvent)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const shouldSkipAutoCheck = useCallback((): boolean => {
    if (!skipIfCheckedRecently) return false

    const lastCheckTime = parseInt(localStorage.getItem(LAST_CHECK_KEY) || '0', 10)
    if (!lastCheckTime) return false

    return Date.now() - lastCheckTime < CHECK_INTERVAL_MS
  }, [skipIfCheckedRecently])

  const isVersionDismissed = useCallback((version: string): boolean => {
    const dismissed = localStorage.getItem(DISMISSED_VERSION_KEY)
    return dismissed === version
  }, [])

  /**
   * 后台下载更新（桌面端 + 移动端通用）
   *
   * - 桌面端：通过 performBackgroundDownload → tauri-plugin-updater download()
   * - 移动端：通过 performBackgroundDownload(url) → tauri-plugin-upload download() 下载到缓存
   *
   * 注意：必须用 updateInfoRef.current 而非 updateInfo state。
   * doCheck 中 setUpdateInfo(info) 后 500ms 通过 setTimeout 调用 startDownload，
   * 但 setTimeout 闭包捕获的是旧渲染的 startDownload（此时 updateInfo state 仍为 null）。
   * 用 ref 读取最新值才能避免闭包陷阱，确保下载正常启动。
   */
  const startDownload = useCallback(async (): Promise<void> => {
    const info = updateInfoRef.current
    if (!info) return

    const currentStatus = downloadStatusRef.current
    if (currentStatus === 'downloading' || currentStatus === 'downloaded') return

    setDownloadStatus('downloading')
    downloadStatusRef.current = 'downloading'
    setError(null)
    setProgress(null)

    try {
      // 移动端需要从 updateInfo 拿到 downloadUrl 传给 performBackgroundDownload
      const mobileUrl = isMobile ? info.downloadUrl : undefined
      console.log(
        `[useUpdateChecker] 开始${isMobile ? '移动端' : '桌面端'}后台下载...`,
        isMobile ? `url=${mobileUrl}` : ''
      )

      await performBackgroundDownload((p) => {
        if (mountedRef.current) {
          setProgress(p)
        }
      }, mobileUrl)

      if (mountedRef.current) {
        setDownloadStatus('downloaded')
        downloadStatusRef.current = 'downloaded'
        console.log('[useUpdateChecker] 后台下载完成')
      }
    } catch (err: any) {
      console.error('[useUpdateChecker] 后台下载失败:', err)
      if (mountedRef.current) {
        setError(err?.message || '下载失败')
        setDownloadStatus('idle')
        downloadStatusRef.current = 'idle'
      }
    }
  }, [isMobile])

  /**
   * 安装已下载的更新（桌面端 + 移动端通用）
   *
   * - 桌面端：performInstallDownloaded → tauri-plugin-updater install() + relaunch
   * - 移动端：performInstallDownloaded → android-installer install() 启动系统安装器
   */
  const installDownloaded = useCallback(async (): Promise<void> => {
    const currentStatus = downloadStatusRef.current
    if (currentStatus !== 'downloaded') return

    setDownloadStatus('installing')
    downloadStatusRef.current = 'installing'
    setError(null)

    try {
      console.log(
        `[useUpdateChecker] 开始安装已下载的更新 (${isMobile ? '移动端' : '桌面端'})...`
      )
      // 清除持久化状态：
      // - 桌面端：安装过程会重启，先清理避免重启后误认为还有更新
      // - 移动端：系统安装器杀掉进程后也不会再用到
      persistDownloadStatus('idle')
      await performInstallDownloaded()
      // 桌面端安装成功后会重启应用，不会执行到这里
      // 移动端安装成功后系统会杀掉本进程，也通常不会执行到这里
    } catch (err: any) {
      console.error('[useUpdateChecker] 安装失败:', err)
      if (mountedRef.current) {
        setError(err?.message || '安装失败')
        setDownloadStatus('downloaded') // 保持下载完成状态，允许重试
        downloadStatusRef.current = 'downloaded'
      }
    }
  }, [isMobile, persistDownloadStatus])

  const doCheck = useCallback(async (force = false): Promise<void> => {
    if (!mountedRef.current) return
    setIsChecking(true)
    setError(null)

    try {
      console.log('[useUpdateChecker] 检查更新... force=', force)
      const info = await checkForUpdate()

      if (!mountedRef.current) return

      if (info && info.hasUpdate) {
        const dismissedVersion = localStorage.getItem(DISMISSED_VERSION_KEY)
        console.log('[useUpdateChecker] 检测到新版本:', {
          version: info.version,
          forceUpdate: info.forceUpdate,
          dismissedVersion,
          isDismissed: dismissedVersion === info.version,
        })

        // 强制更新：无视 dismiss 记录
        if (!info.forceUpdate && isVersionDismissed(info.version)) {
          // 用户已忽略该版本：仍然设置 updateInfo（让前端知道有新版本），
          // 但标记 dismissed=true，App.tsx 据此决定是否自动弹窗
          console.log('[useUpdateChecker] 用户已忽略该版本 → 静默返回 (dismissed_version =', dismissedVersion, ')')
          setUpdateInfo({ ...info, dismissed: true })
          setDownloadStatus('idle')
          downloadStatusRef.current = 'idle'
        } else {
          console.log('[useUpdateChecker] 发现新版本，显示更新弹窗:', info.version)
          setUpdateInfo(info)
          setDownloadStatus('idle')
          downloadStatusRef.current = 'idle'
          autoDownloadStartedRef.current = false

          // 检测到新版本后自动后台下载（桌面端 + 移动端，非强制更新）
          // - 桌面端：autoDownloadOnDesktop 开关控制（默认 true）
          // - 移动端：默认启用自动后台下载（类似桌面端体验，先下载好再提示安装）
          const shouldAutoDownload = info.forceUpdate
            ? false
            : isMobile
              ? true
              : autoDownloadOnDesktop

          if (shouldAutoDownload) {
            // 延迟一小段时间，让 UI 先渲染
            setTimeout(() => {
              if (!autoDownloadStartedRef.current && mountedRef.current) {
                autoDownloadStartedRef.current = true
                console.log(
                  `[useUpdateChecker] 自动启动后台下载 (${isMobile ? '移动端' : '桌面端'}, forceUpdate=${info.forceUpdate})`
                )
                startDownload()
              }
            }, 500)
          }
        }
      } else {
        console.log('[useUpdateChecker] 没有发现新版本. info=', info)
        setUpdateInfo(null)
        setDownloadStatus('idle')
        downloadStatusRef.current = 'idle'
        autoDownloadStartedRef.current = false
        if (force) {
          // 手动触发检查且无更新时，由调用方给出 "已是最新版本" 反馈
          console.log('[useUpdateChecker] 手动触发检查: 当前已是最新版本')
        }
      }

      // 更新最后检查时间
      localStorage.setItem(LAST_CHECK_KEY, String(Date.now()))
    } catch (err: any) {
      console.warn('[useUpdateChecker] 检查更新失败:', err)
      if (!mountedRef.current) return
      setError(err?.message || '检查更新失败')
    } finally {
      if (mountedRef.current) {
        setIsChecking(false)
      }
    }
  }, [isVersionDismissed, isMobile, autoDownloadOnDesktop, startDownload])

  const checkNow = useCallback(async (): Promise<void> => {
    await doCheck(true)
  }, [doCheck])

  const dismiss = useCallback((): void => {
    if (updateInfo) {
      localStorage.setItem(DISMISSED_VERSION_KEY, updateInfo.version)
    }
    setUpdateInfo(null)
    setDownloadStatus('idle')
    downloadStatusRef.current = 'idle'
    autoDownloadStartedRef.current = false
  }, [updateInfo])

  const applyUpdate = useCallback(async (): Promise<void> => {
    if (!updateInfo) return

    setIsUpdating(true)
    setError(null)
    setProgress(null)

    try {
      const currentStatus = downloadStatusRef.current

      // 如果已有下载完成的更新，直接安装
      if (currentStatus === 'downloaded') {
        console.log('[useUpdateChecker] 直接安装已下载的更新')
        await installDownloaded()
        return
      }

      // 如果正在下载，不允许重复点击
      if (currentStatus === 'downloading') {
        console.log('[useUpdateChecker] 正在下载中，忽略点击')
        return
      }

      // 否则执行传统的下载 + 安装流程
      console.log('[useUpdateChecker] 执行传统下载安装流程')
      await performUpdate(
        (p) => {
          if (mountedRef.current) {
            setProgress(p)
          }
        },
        updateInfo.downloadUrl // 移动端需要传入 URL
      )
      // 桌面端 performUpdate 完成后会 relaunch，不会执行到这里
      // 移动端会打开浏览器下载，这里关闭 Modal
      if (mountedRef.current) {
        setUpdateInfo(null)
      }
    } catch (err: any) {
      console.error('[useUpdateChecker] 应用更新失败:', err)
      if (mountedRef.current) {
        setError(err?.message || '更新失败')
      }
    } finally {
      if (mountedRef.current) {
        setIsUpdating(false)
      }
    }
  }, [updateInfo, installDownloaded])

  // 挂载时延迟自动检查
  useEffect(() => {
    mountedRef.current = true

    if (!checkOnMount) return
    if (checkedRef.current) return
    if (shouldSkipAutoCheck()) return

    const timer = setTimeout(() => {
      checkedRef.current = true
      doCheck(false)
    }, delayMs)

    return () => {
      clearTimeout(timer)
      mountedRef.current = false
    }
  }, [checkOnMount, delayMs, shouldSkipAutoCheck, doCheck])

  return {
    updateInfo,
    isChecking,
    isUpdating,
    error,
    progress,
    downloadStatus,
    checkNow,
    dismiss,
    applyUpdate,
    startDownload,
    installDownloaded,
    retryDownload: startDownload,
  }
}

/**
 * 比较两个语义化版本号
 * @returns -1 if a < b, 0 if a === b, 1 if a > b
 */
function compareVersions(a: string, b: string): number {
  const parseVersion = (v: string): number[] => {
    return v
      .replace(/^v/, '')
      .split('.')
      .map((part) => parseInt(part, 10) || 0)
  }

  const aParts = parseVersion(a)
  const bParts = parseVersion(b)
  const maxLength = Math.max(aParts.length, bParts.length)

  for (let i = 0; i < maxLength; i++) {
    const aPart = aParts[i] || 0
    const bPart = bParts[i] || 0
    if (aPart < bPart) return -1
    if (aPart > bPart) return 1
  }

  return 0
}
